import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

// ─── DATA ───────────────────────────────────────────────────────────────────

const thumbs = [
  "https://images.unsplash.com/photo-1555215695-3004980ad54e?w=200&q=80",
  "https://images.unsplash.com/photo-1617788138017-80ad40651399?w=200&q=80",
  "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=200&q=80",
  "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=200&q=80",
  "https://images.unsplash.com/photo-1502877338535-766e1452684a?w=200&q=80",
];

const largeImgs = [
  "https://images.unsplash.com/photo-1555215695-3004980ad54e?w=900&q=80",
  "https://images.unsplash.com/photo-1617788138017-80ad40651399?w=900&q=80",
  "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=900&q=80",
  "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=900&q=80",
  "https://images.unsplash.com/photo-1502877338535-766e1452684a?w=900&q=80",
];

const galleryImgs = largeImgs.slice(0, 4);

const specs = [
  { icon: "⚙️", label: "Transmission", value: "Auto" },
  { icon: "🛣️", label: "Mileage", value: "24 KMS" },
  { icon: "🔧", label: "Steering", value: "Auto" },
  { icon: "🚗", label: "Make", value: "Audi" },
  { icon: "📅", label: "Model Year", value: "2019" },
  { icon: "🛑", label: "Brake", value: "ABS" },
  { icon: "🚘", label: "Body", value: "Sedan" },
  { icon: "❄️", label: "AC", value: "Air Conditioned" },
  { icon: "⚡", label: "Engine (Hp)", value: "3,000" },
  { icon: "⛽", label: "Fuel Type", value: "Diesel" },
  { icon: "🚪", label: "Doors", value: "4" },
  { icon: "🔑", label: "Access", value: "Remote" },
];

const extraServices = [
  { icon: "🛰️", label: "GPS Navigation Systems" },
  { icon: "📶", label: "Wi-Fi Hotspot" },
  { icon: "✅", label: "Express Check-in/out" },
  { icon: "🆘", label: "Roadside Assistance" },
  { icon: "👶", label: "Child Safety Seats" },
  { icon: "⛽", label: "Fuel Options" },
];

const features = [
  "Multi-zone A/C",
  "Heated front seats",
  "Android Auto",
  "Navigation system",
  "Premium sound system",
  "Bluetooth",
  "Keyless Start",
  "Memory seat",
  "6 Cylinders",
  "Adaptive Control",
  "Intermittent wipers",
  "4 power windows",
];

const faqs = [
  {
    q: "How old do I need to be to rent a car?",
    a: "We offer a diverse fleet of vehicles to suit every need, including compact cars, sedans, SUVs and luxury vehicles. You can browse our selection online or contact us for assistance.",
  },
  {
    q: "What documents do I need to rent a car?",
    a: "We offer a diverse fleet of vehicles to suit every need, including compact cars, sedans, SUVs and luxury vehicles. You can browse our selection online or contact us for assistance.",
  },
  {
    q: "What types of vehicles are available for rent?",
    a: "We offer a diverse fleet of vehicles to suit every need, including compact cars, sedans, SUVs and luxury vehicles. You can browse our selection online or contact us for assistance.",
  },
  {
    q: "Can I rent a car with a debit card?",
    a: "We offer a diverse fleet of vehicles to suit every need, including compact cars, sedans, SUVs and luxury vehicles. You can browse our selection online or contact us for assistance.",
  },
];

const reviews = [
  {
    name: "Joseph Massey",
    time: "2 days ago",
    rating: 5,
    title: "Unforgettable Stay!",
    text: "It was a good location however the cocoon concept was weird. No tables, chairs etc was difficult as everything went on the floor.",
    likes: 21,
    dislikes: 50,
    hearts: 45,
    avatar: "https://i.pravatar.cc/60?img=11",
    imgs: galleryImgs.slice(0, 3),
  },
  {
    name: "Jeffrey Jones",
    time: "3 days ago",
    rating: 4,
    title: "Excellent service!",
    text: "From the moment we arrived, the staff made us feel welcome. The rooms were immaculate, and every detail was thoughtfully arranged.",
    likes: 15,
    dislikes: 30,
    hearts: 52,
    avatar: "https://i.pravatar.cc/60?img=22",
  },
  {
    name: "Jessie Alves",
    time: "5 days ago",
    rating: 5,
    title: "Convenient Location!",
    text: "The location was perfect for exploring the city, and the views from our room were breathtaking. It made our trip so much more enjoyable.",
    likes: 13,
    dislikes: 38,
    hearts: 75,
    avatar: "https://i.pravatar.cc/60?img=35",
    reply: {
      name: "Adrian Hendriques",
      rating: 2,
      text: "Thank you so much for your kind words! We're thrilled to hear that our location and views made your trip even more enjoyable.",
      avatar: "https://i.pravatar.cc/60?img=47",
    },
  },
];

const tabs = [
  "Description",
  "Specification",
  "Services",
  "Gallery",
  "Features",
  "FAQ",
  "Reviews",
];

// ─── SUBCOMPONENTS ────────────────────────────────────────────────────────────

function Stars({ rating }) {
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg
          key={i}
          className={`w-3.5 h-3.5 ${i <= rating ? "text-amber-400" : "text-slate-600"}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </span>
  );
}

function AccordionSection({ id, title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div id={id} className="mb-4 rounded-2xl overflow-hidden border">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left  font-semibold text-xl transition-colors"
      >
        <span>{title}</span>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="px-5 pb-5 pt-1">{children}</div>}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function CarDetailsPage() {
  const [activeImg, setActiveImg] = useState(0);
  const [activeTab, setActiveTab] = useState("Description");
  const [openFaq, setOpenFaq] = useState(0);
  const [saved, setSaved] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [thumbStart, setThumbStart] = useState(0);
  const visibleThumbs = 4;

  const scrollThumbs = (dir) => {
    setThumbStart((prev) =>
      Math.max(0, Math.min(thumbs.length - visibleThumbs, prev + dir)),
    );
  };

  const scrollToSection = (tab) => {
    setActiveTab(tab);
    const map = {
      Description: "sec-desc",
      Specification: "sec-spec",
      Services: "sec-services",
      Gallery: "sec-gallery",

      FAQ: "sec-faq",
      Reviews: "sec-reviews",
    };
    const el = document.getElementById(map[tab]);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const [selectedCabPackage, setSelectedCabpackage] = useState(null);
  const { id } = useParams();

  const fetchCabPackageById = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/cab-package/get-by-id/${id}`,
      );
      setSelectedCabpackage(response?.data);
      console.log(response);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchCabPackageById();
  }, []);

  return (
    <div className="">
      {/* ── BANNER ────────────────────────────────────────────── */}
      <div className="relative h-80 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1400&q=80"
          alt="Banner"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 " />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white drop-shadow-lg">
            Car Details
          </h1>
          <div className="flex items-center gap-2 mt-2 text-slate-300 text-sm">
            <span>🏠</span>
            <span className="text-slate-400">›</span>
            <span>Cars</span>
            <span className="text-slate-400">›</span>
            <span className="text-amber-400 font-medium">Car Details</span>
          </div>
        </div>
      </div>

      {/* ── CAR INFO BAR ─────────────────────────────────────── */}
      <div className="px-4 sm:px-8 lg:px-20 py-4 md:py-10">
        <div className="mx-auto">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl font-black tracking-tight">
                  Audi A3 2019
                </h2>
                <span className="flex items-center gap-1.5 bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-bold px-2.5 py-1 rounded-full">
                  ⭐ 4.5{" "}
                  <span className="text-slate-400 font-normal">
                    (500 Reviews)
                  </span>
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm">
                <span className="flex items-center gap-1.5">
                  🚗 <span className="">Sedan</span>
                </span>
                <span className="flex items-center gap-1.5">
                  📍 <span className="">Ciutat Vella, Barcelona</span>
                  <a href="#" className="text-amber-400 underline text-xs ml-1">
                    View Location
                  </a>
                </span>
                <span className="flex items-center gap-1.5 text-emerald-400">
                  ✅ Dropoff
                </span>
                <span className="flex items-center gap-1.5 text-emerald-400">
                  ✅ Airport Pickup
                </span>
                <span className="flex items-center gap-1.5 text-emerald-400">
                  ✅ Hourly Package
                </span>
              </div>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <button
                  onClick={() => setSaved(!saved)}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${saved ? "bg-rose-500/20 border-rose-500/40 text-rose-400" : "border-slate-700 text-slate-400 hover:border-rose-500/40 hover:text-rose-400"}`}
                >
                  {saved ? "❤️" : "🤍"} {saved ? "Saved" : "Save"}
                </button>
                <span className="text-xs bg-slate-800 border border-slate-700 text-slate-300 px-3 py-1.5 rounded-full font-medium">
                  No Of Bookings : 96
                </span>
                <div>
                  <p className="text-xs text-slate-500">Starts From</p>
                  <p className="text-lg font-black text-amber-400">
                    $500{" "}
                    <span className="text-sm text-slate-500 font-normal">
                      / Night
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ─────────────────────────────────────── */}
      <div className="w-full mx-auto px-4 sm:px-8 lg:px-20 pb-10">
        <div className="flex flex-col xl:flex-row gap-8">
          {/* ── LEFT COLUMN ──────────────────────────────────── */}
          <div className="flex-1 min-w-0">
            {/* ── IMAGE SLIDER ─────────────────────────────── */}
            <div className=" rounded-2xl overflow-hidden mb-6">
              <div className="flex gap-3 p-4">
                {/* Vertical thumbnails */}
                <div className="flex flex-col gap-2 items-center bg-white p-2 rounded-2xl shadow-lg">
                  <button
                    onClick={() => scrollThumbs(-1)}
                    disabled={thumbStart === 0}
                    className="w-12 h-12 flex items-center justify-center disabled:opacity-30 transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M18 15l-6-6-6 6" />
                    </svg>
                  </button>
                  <div className="flex flex-col gap-2">
                    {thumbs
                      .slice(thumbStart, thumbStart + visibleThumbs)
                      .map((img, i) => {
                        const realIdx = i + thumbStart;
                        return (
                          <button
                            key={realIdx}
                            onClick={() => setActiveImg(realIdx)}
                            className={`w-[120px] h-[100px] rounded-xl overflow-hidden border-2 transition-all duration-200 flex-shrink-0 ${activeImg === realIdx ? "border-amber-400 scale-105" : "border-slate-700 hover:border-slate-500"}`}
                          >
                            <img
                              src={img}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </button>
                        );
                      })}
                  </div>
                  <button
                    onClick={() => scrollThumbs(1)}
                    disabled={thumbStart + visibleThumbs >= thumbs.length}
                    className="w-12 h-12 flex items-center justify-center disabled:opacity-30 transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </div>

                {/* Main image */}
                <div
                  className="flex-1 relative rounded-xl overflow-hidden"
                  style={{ minHeight: 300 }}
                >
                  <img
                    src={largeImgs[activeImg]}
                    alt="Car"
                    className="w-full h-full object-cover transition-opacity duration-300"
                    style={{ minHeight: 300 }}
                  />
                  <div className="absolute bottom-3 left-3 flex gap-1.5">
                    {largeImgs.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImg(i)}
                        className={`rounded-full transition-all duration-200 ${i === activeImg ? "w-5 h-2 bg-amber-400" : "w-2 h-2 bg-white/40 hover:bg-white/70"}`}
                      />
                    ))}
                  </div>
                  <button className="absolute top-3 right-3 bg-black/50 backdrop-blur text-white text-sm font-semibold px-3 py-1.5 rounded-full border border-white/20 hover:bg-black/70 transition-colors">
                    🖼 See All
                  </button>
                </div>
              </div>
            </div>

            {/* ── STICKY TABS ──────────────────────────────── */}
            <div className="sticky top-0 z-20  py-2 mb-6">
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => scrollToSection(tab)}
                    className={`flex-shrink-0 text-xs font-semibold px-4 py-2 rounded-full border transition-all duration-200 ${activeTab === tab ? "bg-amber-400 text-black border-amber-400" : "border-slate-700  hover:border-slate-500 hover:text-white"}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* ── SECTIONS ─────────────────────────────────── */}

            {/* Services */}
            <AccordionSection id="sec-services" title="Extra Services">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {extraServices.map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3  rounded-xl px-4 py-3 border  hover:border-amber-500/30 transition-colors"
                  >
                    <span className="text-xl">{s.icon}</span>
                    <span className="text-sm ">{s.label}</span>
                  </div>
                ))}
              </div>
            </AccordionSection>

            {/* Description */}
            <AccordionSection id="sec-desc" title="Description">
              <p className=" text-lg leading-relaxed mb-3">
                The Audi A3 2019 combines sporty design with premium comfort,
                featuring a turbocharged engine, advanced driver assistance
                systems, and a refined interior with cutting-edge technology.
                Ideal for both city drives and long journeys, it delivers an
                exceptional driving experience.
              </p>
              <p className=" text-lg leading-relaxed">
                Each rental includes full insurance coverage, roadside
                assistance, and 24/7 customer support. The vehicle is
                meticulously maintained and sanitized between rentals for your
                comfort and safety.
              </p>
            </AccordionSection>

            {/* Specification */}
            <AccordionSection id="sec-spec" title="Specification">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {specs.map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-xl px-4 py-3 border  hover:border-amber-500/30 transition-colors"
                  >
                    <span className="text-xl flex-shrink-0">{s.icon}</span>
                    <div>
                      <p className="text-xs  font-medium">{s.label}</p>
                      <p className="text-sm text-yellow-300 font-semibold">
                        {s.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionSection>

            {/* Features */}
            <AccordionSection id="sec-features" title="Features">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {features.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3  rounded-xl px-4 py-3 border  hover:border-emerald-500/30 transition-colors"
                  >
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-xs flex-shrink-0">
                      ✓
                    </span>
                    <span className="text-sm ">{f}</span>
                  </div>
                ))}
              </div>
            </AccordionSection>

            {/* Gallery */}
            <AccordionSection id="sec-gallery" title="Gallery">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {galleryImgs.map((img, i) => (
                  <div
                    key={i}
                    className="rounded-xl overflow-hidden aspect-square border border-slate-700 hover:border-amber-400/50 transition-colors cursor-pointer group"
                  >
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            </AccordionSection>

            {/* FAQ */}
            <AccordionSection id="sec-faq" title="Frequently Asked Questions">
              <div className="space-y-2">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-slate-700 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium transition-colors"
                    >
                      <span>{faq.q}</span>
                      <svg
                        className={`w-4 h-4  transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openFaq === i && (
                      <div className="px-4 pb-4 pt-1 text-sm leading-relaxed ">
                        {faq.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </AccordionSection>

            {/* Reviews */}
            <AccordionSection id="sec-reviews" title="Reviews">
              {/* Rating summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className=" rounded-2xl  p-5 text-center flex flex-col items-center justify-center">
                  <p className="t text-sm font-medium mb-2">
                    Customer Reviews & Ratings
                  </p>
                  <p className="text-5xl font-black text-white mb-2">
                    4.9 <span className="text-lg text-slate-500">/ 5.0</span>
                  </p>
                  <Stars rating={5} />
                  <p className="text-sm mt-2">Based On 2,459 Reviews</p>
                </div>
                <div className="rounded-2xl border border-slate-700 p-5 space-y-3">
                  {[
                    { s: 5, w: "90%", count: 247 },
                    { s: 4, w: "80%", count: 145 },
                    { s: 3, w: "70%", count: 600 },
                    { s: 2, w: "60%", count: 560 },
                    { s: 1, w: "40%", count: 400 },
                  ].map((r) => (
                    <div key={r.s} className="flex items-center gap-3">
                      <span className="text-sm w-14 flex-shrink-0">
                        {r.s} Star
                      </span>
                      <div className="flex-1 rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all"
                          style={{ width: r.w }}
                        />
                      </div>
                      <span className="text-xs  w-8 text-right">{r.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Review cards */}
              <div className="space-y-4 mb-6">
                {reviews.map((r, i) => (
                  <div key={i} className="rounded-2xl p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={r.avatar}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        />
                        <div>
                          <p className="font-semibold text-sm">{r.name}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs">{r.time}</span>
                            <span className="bg-amber-500/20 text-amber-400 text-xs font-bold px-2 py-0.5 rounded-full">
                              {r.rating}.0
                            </span>
                            <span className="text-xs text-slate-400">
                              {r.title}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button className="text-xs border border-slate-600  hover:border-slate-500 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0">
                        ↩ Reply
                      </button>
                    </div>
                    <p className="text-sm  leading-relaxed mb-3">{r.text}</p>
                    {r.imgs && (
                      <div className="flex gap-2 mb-3">
                        {r.imgs.map((img, j) => (
                          <div
                            key={j}
                            className="w-14 h-14 rounded-xl overflow-hidden border border-slate-700"
                          >
                            <img
                              src={img}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-sm">
                      <button className="flex items-center gap-1 hover:text-white transition-colors">
                        👍 {r.likes}
                      </button>
                      <button className="flex items-center gap-1 hover:text-white transition-colors">
                        👎 {r.dislikes}
                      </button>
                      <button className="flex items-center gap-1 hover:text-rose-400 transition-colors">
                        ❤️ {r.hearts}
                      </button>
                    </div>
                    {r.reply && (
                      <div className="mt-4 ml-6 border border-slate-600/50 rounded-xl p-3">
                        <div className="flex items-center gap-3 mb-2">
                          <img
                            src={r.reply.avatar}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <div>
                            <p className="font-semibold text-sm">
                              {r.reply.name}
                            </p>
                            <span className=" text-xs px-1.5 py-0.5 rounded-full">
                              {r.reply.rating}.0 ⭐
                            </span>
                          </div>
                        </div>
                        <p className="text-xs  leading-relaxed">
                          {r.reply.text}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="text-center">
                <button className="bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm px-6 py-2.5 rounded-full transition-colors">
                  See all 4,078 reviews →
                </button>
              </div>
            </AccordionSection>

            {/* Leave a Review */}
            <AccordionSection title="Leave A Review">
              <div>
                <p className="text-sm  mb-3">
                  Your Rating <span className="text-rose-400">*</span>
                </p>
                <div className="flex gap-1 mb-5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      onClick={() => setRating(s)}
                      onMouseEnter={() => setHoverRating(s)}
                      onMouseLeave={() => setHoverRating(0)}
                      className={`text-2xl transition-transform hover:scale-110 ${s <= (hoverRating || rating) ? "text-amber-400" : "text-slate-600"}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-xs  mb-1.5 block">
                      Name <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full  rounded-xl px-4 py-2.5 text-sm  placeholder-slate-600 focus:outline-none focus:border-amber-400 transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="text-xs  mb-1.5 block">
                      Email <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="email"
                      className="w-full rounded-xl px-4 py-2.5 text-sm  placeholder-slate-600 focus:outline-none focus:border-amber-400 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="text-xs  mb-1.5 block">
                    Write Your Review <span className="text-rose-400">*</span>
                  </label>
                  <textarea
                    rows="4"
                    className="w-full rounded-xl px-4 py-2.5 text-sm  placeholder-slate-600 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                    placeholder="Share your experience..."
                  />
                </div>
                <div className="text-center">
                  <button className="bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm px-8 py-2.5 rounded-full transition-colors">
                    ✏️ Submit Review
                  </button>
                </div>
              </div>
            </AccordionSection>
          </div>

          {/* ── RIGHT STICKY COLUMN ──────────────────────────── */}
          <div className="xl:w-80 flex-shrink-0">
            <div className="sticky top-4 space-y-4">
              {/* Booking Card */}
              <div className="border rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-amber-500 to-amber-400 px-5 py-4">
                  <p className="text-black text-xs font-semibold uppercase tracking-wider">
                    Starts From
                  </p>
                  <p className="text-black text-3xl font-black">
                    $500{" "}
                    <span className="text-sm font-normal opacity-70">
                      / Night
                    </span>
                  </p>
                </div>
                <div className="p-5 space-y-3">
                  <div>
                    <label className="text-sm mb-1.5 block">Pick-Up Date</label>
                    <input
                      type="date"
                      className="w-full  border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-sm mb-1.5 block">
                      Drop-Off Date
                    </label>
                    <input
                      type="date"
                      className="w-full  border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-sm mb-1.5 block">
                      Pick-Up Location
                    </label>
                    <select className="w-full border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-300 focus:outline-none focus:border-amber-400 transition-colors">
                      <option>Ciutat Vella, Barcelona</option>
                      <option>El Born, Barcelona</option>
                      <option>Gracia, Barcelona</option>
                    </select>
                  </div>
                  <div className=" border border-slate-700 rounded-xl p-4 space-y-2 text-sm">
                    <div className="flex justify-between text-slate-400">
                      <span>$500 × 3 nights</span>
                      <span className="">$1,500</span>
                    </div>
                    <div className="flex justify-between ">
                      <span>Service Fee</span>
                      <span className="">$75</span>
                    </div>
                    <div className="border-t border-slate-700 pt-2 flex justify-between font-bold">
                      <span>Total</span>
                      <span className="text-amber-400">$1,575</span>
                    </div>
                  </div>
                  <button className="w-full bg-amber-400 hover:bg-amber-300 text-black font-black py-3 rounded-xl transition-colors text-sm tracking-wide">
                    Book Now →
                  </button>
                  <button className="w-full border border-slate-600 hover:border-slate-500 font-semibold py-2.5 rounded-xl transition-colors text-sm">
                    🤍 Save for Later
                  </button>
                </div>
              </div>

              {/* Host Card */}
              <div className="border border-slate-700 rounded-2xl p-5">
                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-3">
                  Hosted By
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src="https://i.pravatar.cc/60?img=15"
                    alt=""
                    className="w-12 h-12 rounded-full object-cover border-2 border-amber-400/30"
                  />
                  <div>
                    <p className="font-bold text-sm text-white">
                      Carlos Mendez
                    </p>
                    <p className="text-xs text-slate-500">Member since 2021</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Stars rating={5} />
                      <span className="text-xs text-slate-500">4.9</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                  {[
                    { label: "Trips", value: "96" },
                    { label: "Response", value: "~1hr" },
                    { label: "Accept", value: "98%" },
                  ].map((s) => (
                    <div key={s.label} className=" rounded-xl py-2">
                      <p className="text-amber-400 font-bold text-sm">
                        {s.value}
                      </p>
                      <p className="text-xs">{s.label}</p>
                    </div>
                  ))}
                </div>
                <button className="w-full border border-slate-600 hover:border-amber-400/50 hover:text-amber-400 text-slate-400 text-sm font-semibold py-2.5 rounded-xl transition-colors">
                  💬 Contact Host
                </button>
              </div>

              {/* Quick Facts */}
              <div className="border border-slate-700 rounded-2xl p-5">
                <p className="text-sm font-semibold uppercase tracking-wider mb-3">
                  Quick Facts
                </p>
                <div className="space-y-2.5">
                  {[
                    { label: "Category", value: "Luxury Sedan" },
                    { label: "Year", value: "2019" },
                    { label: "Fuel", value: "Diesel" },
                    { label: "Seats", value: "5 Passengers" },
                    { label: "Transmission", value: "Automatic" },
                  ].map((f) => (
                    <div key={f.label} className="flex justify-between text-sm">
                      <span className="text-slate-500">{f.label}</span>
                      <span className="text-white font-medium">{f.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
